import { Calendar, DollarSign, Edit2, Save, X, Trash2 } from "lucide-react";
import StatusBadge from "./StatusBadge";
import React, { useCallback, useState } from "react";
import Swal from "sweetalert2";
import { removeQuotaFromPayment } from "../../../../services/quotasService";

const QuotaItem = React.memo(({ 
  cuota, 
  isSelected, 
  onSelection, 
  formatCurrency, 
  formatDate,
  userRole, // Nuevo prop para el rol del usuario
  onUpdateAmount, // Nuevo prop para manejar la actualización del monto
  onQuotaPaymentRemoved, // Callback cuando se elimina el pago de una cuota
  empresaNombre // Nombre de la empresa
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editAmount, setEditAmount] = useState(cuota.amount);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);

  const isAdmin = userRole === 'admin' || userRole === 'administrador';

  const handleCheckboxChange = useCallback(() => {
    onSelection(cuota.id);
  }, [cuota.id, onSelection]);

  const handleEditClick = useCallback(() => {
    // Convertir a entero sin decimales
    const integerAmount = Math.floor(parseFloat(cuota.amount) || 0);
    setEditAmount(integerAmount.toString());
    setIsEditing(true);
  }, [cuota.amount]);

  const handleCancelEdit = useCallback(() => {
    // Convertir a entero sin decimales
    const integerAmount = Math.floor(parseFloat(cuota.amount) || 0);
    setEditAmount(integerAmount.toString());
    setIsEditing(false);
  }, [cuota.amount]);

  const handleSaveEdit = useCallback(async () => {
    if (!editAmount || parseFloat(editAmount) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Monto inválido',
        text: 'Por favor, ingrese un monto válido mayor a 0.',
        confirmButtonText: 'Aceptar',
      });
      return;
    }
    const confirmation = await Swal.fire({
      title: 'Confirmar actualización',
      text: `¿Está seguro de que desea actualizar el monto a ${formatCurrency(parseFloat(editAmount))}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmation.isConfirmed) return;
    setIsLoading(true);
    try {
      await onUpdateAmount(cuota.id, parseFloat(editAmount));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating amount:', error);
      alert('Error al actualizar el monto. Por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  }, [cuota.id, editAmount, onUpdateAmount]);

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Solo permite dígitos 0-9
    setEditAmount(value);
  };

  // Verificar si hay cambios para habilitar el botón guardar
  // Comparar con el valor entero original
  const originalIntegerAmount = Math.floor(parseFloat(cuota.amount) || 0);
  const hasChanges = editAmount !== '' && parseInt(editAmount || '0') !== originalIntegerAmount;

  // Manejar eliminación del pago de esta cuota
  const handleRemovePayment = useCallback(async () => {
    const confirmation = await Swal.fire({
      title: '¿Eliminar pago de esta cuota?',
      html: `
        <div class="text-left">
          <p class="mb-2">Esta acción eliminará el pago de la cuota <strong>${cuota.period} ${cuota.year}</strong> de <strong>${empresaNombre}</strong> y la revertirá a estado <strong>pendiente</strong>.</p>
          <p class="mb-2"><strong>Monto:</strong> ${formatCurrency(parseFloat(cuota.amount))}</p>
          <p class="text-sm text-gray-600 mb-2">Si esta era la única cuota del pago, el pago completo será eliminado.</p>
          <p class="text-red-600 font-semibold">Esta acción no se puede deshacer.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar pago',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#DC3465',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) return;

    try {
      setIsDeletingPayment(true);
      const result = await removeQuotaFromPayment(cuota.id);

      if (result.error) {
        await Swal.fire({
          icon: 'error',
          title: 'Error al eliminar pago',
          text: result.message || 'No se pudo eliminar el pago de la cuota',
        });
        return;
      }

      await Swal.fire({
        icon: 'success',
        title: 'Pago eliminado',
        text: result.message || 'La cuota ha vuelto a estado pendiente.',
        timer: 2000,
      });

      // Recargar datos si hay callback
      if (onQuotaPaymentRemoved) {
        onQuotaPaymentRemoved();
      }
    } catch (error) {
      console.error('Error al eliminar pago de cuota:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al eliminar el pago. Por favor, intenta nuevamente.',
      });
    } finally {
      setIsDeletingPayment(false);
    }
  }, [cuota.id, cuota.period, cuota.year, cuota.amount, formatCurrency, onQuotaPaymentRemoved, empresaNombre]);

  return (
    <div
      className={`border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow ${
        cuota.status === 'pending' && isSelected ? 'bg-blue-50 border-blue-300' : 'bg-gray-50/50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {cuota.status === 'pending' && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleCheckboxChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
          )}
          
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <DollarSign className="text-[#0488AF]" size={20} />
          </div>

          <div>
            {/* Periodo destacado */}
            <div className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-1">
              {cuota.period}
            </div>

            <div className="flex items-center gap-3 mb-1">
              {/* Monto editable para administradores */}
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editAmount}
                    onChange={handleAmountChange}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isLoading}
                  />
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleSaveEdit}
                      disabled={isLoading || !hasChanges}
                      className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={hasChanges ? "Guardar cambios" : "Sin cambios para guardar"}
                    >
                      <Save size={16} />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isLoading}
                      className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      title="Cancelar edición"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">
                    {formatCurrency(parseFloat(cuota.amount))}
                  </span>
                  {isAdmin && cuota.status === 'pending' && (
                    <button
                      onClick={handleEditClick}
                      className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Editar monto"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
              )}
              <StatusBadge status={cuota.status} />
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Año:</span> {cuota.year}</p>
              <p><span className="font-medium">Documento:</span> {cuota.id}</p>
            </div>
          </div>
        </div>

        <div className="text-right text-sm text-gray-600">
          <div className="flex items-center gap-1 mb-1">
            <Calendar size={14} />
            <span className="font-medium">Fecha de pago:</span>
          </div>
          <span className={cuota.payment_date ? "text-green-600" : "text-yellow-600"}>
            {formatDate(cuota.payment_date)}
          </span>
          {/* Botón eliminar pago solo para administradores y cuotas pagadas */}
          {isAdmin && cuota.status === 'paid' && cuota.payment_date && (
            <div className="mt-3">
              <button
                onClick={handleRemovePayment}
                disabled={isDeletingPayment}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Eliminar pago de esta cuota y revertir a pendiente"
              >
                {isDeletingPayment ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    Eliminar pago
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default QuotaItem;
